import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Icon, Menu, Popup, Tab } from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { DownloadLink } from "simplydo/react/components/DownloadLink";
import { RichTextViewer } from "simplydo/react/richtext";

import api, { websocketApi } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import CircularButtonWithNumber from "../CircularButtonWithNumber";
import ModalCarousel from "../ModalCarousel";
import Downloads from "./Downloads";
import Notifications from "./Notifications";

const StyledNotificationTray = styled.div`
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "100%" : "500px;")};
  max-height: ${({ theme }) =>
    theme.sizes.isMobile ? null : "600px"} !important;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  z-index: 9999;
`;

type NotificationsType =
  OpenAPI.GET<`/notifications`>["response"]["notifications"];

type NotificationTrayProps = {
  isMobile?: boolean;
  style?: React.CSSProperties;
};

export const useTrayStatus = () => {
  type TrayStatus = { open: boolean; activeIndex: number };

  const defaultState: TrayStatus = { open: false, activeIndex: 0 };
  const openState = useQuery({
    queryKey: ["notificationTray"],
    queryFn: async () => defaultState,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  const setTrayStatus = useCallback((trayStatus: Partial<TrayStatus>) => {
    api.queryClient.setQueryData(
      ["notificationTray"],
      (prevStatus: TrayStatus) => ({ ...prevStatus, ...trayStatus }),
    );
  }, []);

  return [openState.data || defaultState, setTrayStatus] as const;
};

const NotificationTray = ({ isMobile, style }: NotificationTrayProps) => {
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();

  const [trayStatus, setTrayStatus] = useTrayStatus();
  const [page, setPage] = useState(1);
  const [downloadCompleted, setDownloadCompleted] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [pendingTasks, setPendingTasks] = useState<string[]>([]);
  const [announcementNotification, setAnnouncementNotification] = useState<
    NotificationsType[number] | null
  >(null);

  const userId = user?._id;
  const invitationsQuery = useQuery(
    initUseQuery(api.invitations.get, [], { isEnabled: !!userId }),
  );
  // Non-acceptable invitations aren't really interesting to the user in terms of notification content. They may be able to remove them, but it's not really "notification" worthy that e.g., another admin has invited a user to one of their groups
  const acceptableInvitations = useMemo(
    () =>
      invitationsQuery.data?.invitations.filter(
        (invitation) =>
          !invitation.createdByCurrentUser && invitation.canAccept,
      ) || [],
    [invitationsQuery.data?.invitations],
  );

  useEffect(() => {
    if (!userId) return;
    const notificationSubscription = websocketApi.subscribe(
      `pushNotification-${userId}`,
      () => {
        api.invalidateQueries(api.notifications.get);
      },
    );
    return notificationSubscription.unsubscribe;
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const downloadSubscription = websocketApi.subscribe(
      `downloadCompleted-${userId}`,
      () => {
        setDownloadCompleted(true);
        setTrayStatus({ open: true, activeIndex: 1 });
        api.invalidateQueries(api.users.getDownloads);
      },
    );
    return downloadSubscription.unsubscribe;
  }, [userId, setTrayStatus]);

  const notificationsQuery = useQuery(
    initUseQuery(api.notifications.get, [page], {
      isEnabled: !!userId,
      refetchInterval: ({ state }) => {
        if (
          (state.data?.notifications?.filter(
            (notif) => notif.status === "pending",
          )?.length ?? 0) > 0
        ) {
          return 5000;
        }
        return false;
      },
    }),
  );

  useEffect(() => {
    // We monitor pending tasks to see if any have completed since the last check and if so, we open the tray
    const newPendingTasks =
      notificationsQuery.data?.notifications
        ?.filter((n) => n.status === "pending")
        ?.map((n) => n._id) ?? [];
    if (newPendingTasks.length === 0 && pendingTasks.length === 0) {
      return;
    }
    const hadPendingTasks = pendingTasks.length > 0;
    const hasNewCompletedTasks =
      hadPendingTasks &&
      pendingTasks.some((taskId) => !newPendingTasks.includes(taskId));
    if (hasNewCompletedTasks) {
      setTrayStatus({ open: true, activeIndex: 0 });
    }
    if (
      newPendingTasks.length === pendingTasks.length &&
      newPendingTasks.every((id, index) => id === pendingTasks[index])
    ) {
      return;
    }
    setPendingTasks(newPendingTasks);
  }, [notificationsQuery.data?.notifications, pendingTasks, setTrayStatus]);

  const markAsSeen = useCallback((newNotifications, markAllAsSeen) => {
    api.notifications.markAsSeen(
      newNotifications,
      markAllAsSeen,
      () => {
        api.invalidateQueries(api.notifications.get);
      },
      () => {},
    );
  }, []);

  const markAllAsSeen = useCallback(() => {
    markAsSeen([], true);
  }, [markAsSeen]);
  const notificationContent = (
    <StyledNotificationTray>
      <Tab
        defaultActiveIndex={0}
        activeIndex={trayStatus.activeIndex}
        onTabChange={(_e, { activeIndex }) => {
          setTrayStatus({ activeIndex: activeIndex as number });
        }}
        menu={{ secondary: true, pointing: true }}
        style={{ position: "relative", margin: 0 }}
        panes={[
          {
            menuItem: (
              <Menu.Item>
                <Icon name="bell" />
                Notifications
              </Menu.Item>
            ),
            render: () => (
              <Notifications
                notifications={notificationsQuery.data?.notifications || []}
                total={notificationsQuery.data?.total || 0}
                acceptableInvitations={acceptableInvitations}
                invitations={invitationsQuery.data?.invitations || []}
                page={page}
                setPage={setPage}
                setAnnouncementNotification={setAnnouncementNotification}
                setAnnouncementModalOpen={setAnnouncementModalOpen}
                loading={notificationsQuery.isLoading}
              />
            ),
          },
          {
            menuItem: (
              <Menu.Item>
                <Icon name="download" />
                Downloads
              </Menu.Item>
            ),
            render: () => (
              <Downloads downloadCompleted={downloadCompleted} page={page} />
            ),
          },
        ]}
      />
    </StyledNotificationTray>
  );

  if (isMobile) {
    return notificationContent;
  }
  return (
    <>
      {announcementNotification?.events && (
        <ModalCarousel
          open={announcementModalOpen}
          onClose={() => setAnnouncementModalOpen(false)}
          screens={announcementNotification.events?.map((event) => ({
            title: event.ownerAnnouncement?.subject,
            content: (
              <div>
                {event.ownerAnnouncement.body ?
                  <RichTextViewer content={event.ownerAnnouncement?.body} />
                : <p>{event.ownerAnnouncement?.text}</p>}
                {event.ownerAnnouncement?.attachments?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h5>Attachments</h5>
                    {event.ownerAnnouncement.attachments.map((attachment) => (
                      <DownloadLink
                        key={attachment.url}
                        url={attachment.url}
                        name={attachment.name}
                        className="ui small blue button"
                        style={{ borderRadius: 5 }}
                      >
                        <i className="paperclip icon"></i>
                        {attachment.name}
                      </DownloadLink>
                    ))}
                  </div>
                )}
                <p style={{ color: "grey" }}>
                  {event?.ownerAnnouncement?.createdAt ?
                    ` ${moment(event.ownerAnnouncement.createdAt).format("DD/MM/YY HH:mm")}`
                  : ""}
                </p>
              </div>
            ),
          }))}
          actions={[
            {
              label: "Close",
              icon: "check",
              primary: true,
              onClick: () => {
                setAnnouncementModalOpen(false);
              },
            },
          ]}
        />
      )}

      <Popup
        mountNode={document.getElementById("semantic-modal-mount-node")}
        on="click"
        style={{ width: 550 }}
        positionFixed
        wide="very"
        position="bottom right"
        offset={[5, 0]}
        open={trayStatus.open}
        onClose={() => {
          setDownloadCompleted(false);
          setTrayStatus({ open: false });
        }}
        trigger={
          <div style={style}>
            <CircularButtonWithNumber
              onClick={() => {
                if (!trayStatus.open) {
                  markAllAsSeen();
                }
                if (theme.sizes.isMobile) {
                  if (location.pathname === "/notifications") {
                    navigate("..");
                  } else {
                    navigate("/notifications");
                  }
                } else {
                  if (!trayStatus.open) {
                    api.invalidateQueries(api.users.getDownloads);
                  }
                  setDownloadCompleted(false);
                  setTrayStatus({ open: !trayStatus.open });
                }
              }}
              icon="bell"
              count={
                (
                  notificationsQuery.data?.unseen === 0 &&
                  acceptableInvitations.length !== 0
                ) ?
                  "●"
                : (notificationsQuery.data?.unseen ?? 0)
              }
            />
          </div>
        }
        content={notificationContent}
      />
    </>
  );
};

export default NotificationTray;
